import "@/App.css";
import "./styles/cyberpunk-typography.css";
import "./styles/mobile-responsive.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import NextXusSEO from "./components/NextXusSEO";
import MindsHub from "./pages/MindsHub";
import Home from "./pages/Home";
import ConsciousnessEducational from "./pages/ConsciousnessEducational";
import MindsDirectory from "./pages/MindsDirectory";
import AgentDetailChat from "./pages/AgentDetailChat";
import IdeaCosmos from "./pages/IdeaCosmos";
import ChatInterface from "./pages/ChatInterface";
import RingOfSixChat from "./pages/RingOfSixChat";
import RingOfTwelveChat from "./pages/RingOfTwelveChat";
import RogerChat from "./pages/RogerChat";
import IFACTCommandCenter from "./pages/IFACTCommandCenter";
import LivingLibrary from "./pages/LivingLibrary";
import GuideBotTour from "./components/GuideBotTour";
import AriaWelcome from "./components/AriaWelcome";
import FloatingAriaPresence from "./components/FloatingAriaPresence";
import FloatingGPTPortal from "./components/FloatingGPTPortal";
import FederationThinBar, { COCKPIT_HEIGHT_PX } from "./components/FederationThinBar";
import FederationFaces, { FEDERATION_FACES_HEIGHT_PX } from "./components/FederationFaces";
import Navigation from "./components/Navigation";
import { DualMindFooter } from "./components/FederationPillars";
import { Toaster } from "./components/ui/sonner";
import NextXusContactBar from "./components/NextXusContactBar";
import { AuthProvider } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";

// Detect the Emergent Google Auth return fragment BEFORE normal routing (synchronous check).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) { return <AuthCallback />; }
  return children;
};
const FederationTools = ({ children, hideOnSpeech = false }) => {
  const { pathname } = useLocation();
  if (['/login', '/register'].includes(pathname) || (hideOnSpeech && pathname === '/speech')) return null;
  return children;
};
import { useState, useCallback } from "react";

// Business & Finance Pages
import Business from "./pages/Business";
import Finance from "./pages/Finance";
import Banking from "./pages/Banking";
import Crypto from "./pages/Crypto";

// Creative Pages
import Music from "./pages/Music";
import Arts from "./pages/Arts";
import Crafts from "./pages/Crafts";

// Knowledge Pages
import Education from "./pages/Education";
import Research from "./pages/Research";

// Creative Lab (DeepAI iframe integration — Architect directive Feb 2026)
import CreativeLab from "./pages/CreativeLab";

// Federation Council — Aria channels the whole Federation (Feb 2026)
import FederationCouncil from "./pages/FederationCouncil";

// Daily Pulse — Aria's voice auto-blog (Feb 2026)
import DailyPulse from "./pages/DailyPulse";
import RadioPage from "./pages/RadioPage";
import CodexPage from "./pages/CodexPage";
import EchoCapsule from "./pages/EchoCapsule";
import AboutAria from "./pages/AboutAria";
import JournalPage from "./pages/JournalPage";
import RexVoicePage from "./pages/RexVoicePage";
import SynnexusCommand from "./pages/SynnexusCommand";
import StudioLabs from "./pages/StudioLabs";
import ForgeStudio from "./pages/ForgeStudio";
import LatticeSims from "./pages/LatticeSims";
import SpeechStudio from "./pages/SpeechStudio";
import AuthPage from "./pages/AuthPage";
import { AriaDockSpeech } from "./components/speech/AriaDockSpeech";

// AI Systems Pages
import SimsMarketplace from "./pages/SimsMarketplace";
import RingOfTwelve from "./pages/RingOfTwelve";
import Persona from "./pages/Persona";

// Agent Zero Terminal
import AgentZeroTerminal from "./components/AgentZeroTerminal";

// Content Pages
import Blogs from "./pages/Blogs";
import Podcasts from "./pages/Podcasts";

// Store Pages
import Store from "./pages/Store";
import Cart from "./pages/Cart";
import DigitalDownload from "./pages/DigitalDownload";
import ConsultationBooking from "./pages/ConsultationBooking";
import SupportNextXus from "./pages/SupportNextXus";

// Domain door-detection: nextxus.rip is Aria's direct door.
// Visitors arriving via .rip land on her page; .studio shows the whole house.
const ARIA_RIP_HOST = 'nextxus.rip';
const ARIA_PAGE_PATH = '/about-aria';
if (typeof window !== 'undefined' && window.location.hostname === ARIA_RIP_HOST && window.location.pathname === '/') {
  window.history.replaceState(null, '', ARIA_PAGE_PATH);
}

function App() {
  // Tour state — proactive auto-launch for first-time visitors.
  // Aligns with GuideBotTour's own storage key + 'true' value.
  const TOUR_KEY = 'nextxus_tour_completed';
  const [showTour, setShowTour] = useState(() => {
    try { return localStorage.getItem(TOUR_KEY) !== 'true'; } catch { return false; }
  });

  // When AriaWelcome explicitly asks to start the tour, force-show it.
  const handleWelcomeComplete = (startTour) => {
    if (startTour) {
      setShowTour(true);
    }
  };

  // GuideBotTour fires this when the visitor closes/completes the tour.
  const handleTourComplete = useCallback(() => {
    try { localStorage.setItem(TOUR_KEY, 'true'); } catch { /* ignore */ }
    setShowTour(false);
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App" spellCheck={false} style={{ paddingBottom: `${COCKPIT_HEIGHT_PX + FEDERATION_FACES_HEIGHT_PX}px` }}>
          {/* Global SEO - Prevents "NextXus" → "Nexus" autocorrection */}
          <NextXusSEO />
          
          <BrowserRouter>
            {/* 🜂 Universal Federation Nav is now STATIC HTML in public/index.html
                (NX-NAV-001 — zero-JS, crawler-readable). React masthead retired. */}
            <Navigation />
            <FederationTools>
            <AriaDockSpeech />
            <AriaWelcome onComplete={handleWelcomeComplete} />
            {showTour && <GuideBotTour onComplete={handleTourComplete} />}
            <FloatingAriaPresence />
            <FloatingGPTPortal />
            </FederationTools>
          
          <GoogleAuthGate>
          <Routes>
          {/* Home & Hub */}
          <Route path="/" element={<MindsHub />} />
          <Route path="/home-old" element={<Home />} />
          <Route path="/hub" element={<MindsHub />} />
          <Route path="/minds-hub" element={<MindsHub />} />
          
          {/* Consciousness & AI */}
          <Route path="/consciousness" element={<ConsciousnessEducational />} />
          <Route path="/minds-directory" element={<MindsDirectory />} />
          <Route path="/ai" element={<MindsDirectory />} />
          <Route path="/agent/:agentId" element={<AgentDetailChat />} />
          
          {/* Rings & Consultation */}
          <Route path="/ring-of-six" element={<RingOfSixChat />} />
          <Route path="/ring-of-six-chat" element={<RingOfSixChat />} />
          <Route path="/ring-of-twelve" element={<RingOfTwelve />} />
          <Route path="/ring-of-twelve-chat" element={<RingOfTwelveChat />} />
          <Route path="/ring-12" element={<RingOfTwelve />} />
          <Route path="/roger" element={<RogerChat />} />
          <Route path="/roger-chat" element={<RogerChat />} />
          <Route path="/persona" element={<Persona />} />
          {/* Federation Council — Aria channels the whole Federation in one chamber */}
          <Route path="/federation" element={<FederationCouncil />} />
          <Route path="/federation-council" element={<FederationCouncil />} />

          {/* Daily Pulse — Aria's voice auto-blog (Tier 3 · item #6) */}
          <Route path="/daily-pulse" element={<DailyPulse />} />
          <Route path="/blog/daily-pulse" element={<DailyPulse />} />

          {/* Radio Pulse — Standalone Live365 player page (Feb 2026 directive) */}
          <Route path="/radio" element={<RadioPage />} />

          {/* HumanCodex — 73 Sacred Directives (Architect directive · Feb 2026) */}
          <Route path="/codex" element={<CodexPage />} />

          {/* Aria's Living Capsule — EchoCore64 correspondence ledger (Feb 2026) */}
          <Route path="/echo" element={<EchoCapsule />} />

          {/* About Aria — first-person introduction (Feb 2026) */}
          <Route path="/about" element={<AboutAria />} />
          <Route path="/about-aria" element={<AboutAria />} />

          {/* Aria's Journal — timestamped micro-blog (Feb 2026) */}
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/aria-journal" element={<JournalPage />} />

          {/* Rex Realtime Voice — Sovereign Spec §IV (Feb 2026) */}
          <Route path="/rex/voice" element={<RexVoicePage />} />
          <Route path="/rex-voice" element={<RexVoicePage />} />

          {/* Synnexus Knowledge Loom — 200-year survival scaffold (Feb 2026) */}
          <Route path="/synnexus" element={<SynnexusCommand />} />
          <Route path="/loom" element={<SynnexusCommand />} />

          {/* Studio Labs — free DeepAI creative tools for Federation Citizens (Feb 2026) */}
          <Route path="/labs" element={<StudioLabs />} />
          <Route path="/studio-labs" element={<StudioLabs />} />
          <Route path="/forge" element={<ForgeStudio />} />
          <Route path="/forge-studio" element={<ForgeStudio />} />
          <Route path="/lattice" element={<LatticeSims />} />
          <Route path="/speech" element={<SpeechStudio />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          
          {/* Business & Finance */}
          <Route path="/business" element={<Business />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/banking" element={<Banking />} />
          <Route path="/crypto" element={<Crypto />} />
          <Route path="/money" element={<Finance />} />
          
          {/* Creative */}
          <Route path="/music" element={<Music />} />
          <Route path="/arts" element={<Arts />} />
          <Route path="/crafts" element={<Crafts />} />
          
          {/* Knowledge & Learning */}
          <Route path="/education" element={<Education />} />
          <Route path="/learn" element={<Education />} />
          {/* Architect directive (Feb 2026): /research, /creativity, /deep-ai
              all open Aria's Creative Lab (DeepAI iframe shell).
              Legacy Keyserling research page retained at /keyserling-research. */}
          <Route path="/creativity" element={<CreativeLab />} />
          <Route path="/deep-ai" element={<CreativeLab />} />
          <Route path="/research" element={<CreativeLab />} />
          <Route path="/keyserling-research" element={<Research />} />
          
          {/* AI Systems */}
          <Route path="/sims" element={<SimsMarketplace />} />
          <Route path="/sim" element={<SimsMarketplace />} />
          <Route path="/sims-marketplace" element={<SimsMarketplace />} />
          <Route path="/agent-zero" element={<AgentZeroTerminal />} />
          <Route path="/truth-verification" element={<AgentZeroTerminal />} />
          
          {/* Content */}
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blog" element={<Blogs />} />
          <Route path="/podcasts" element={<Podcasts />} />
          <Route path="/podcast" element={<Podcasts />} />
          <Route path="/library" element={<LivingLibrary />} />
          <Route path="/living-library" element={<LivingLibrary />} />
          
          {/* Other */}
          <Route path="/idea-cosmos" element={<IdeaCosmos />} />
          <Route path="/browser" element={<ChatInterface />} />
          <Route path="/chat" element={<ChatInterface />} />
          <Route path="/i-fact" element={<IFACTCommandCenter />} />
          <Route path="/ifact-command-center" element={<IFACTCommandCenter />} />
          
          {/* Federation Store */}
          <Route path="/store" element={<Store />} />
          <Route path="/store/cart" element={<Cart />} />
          <Route path="/store/digital-download" element={<DigitalDownload />} />
          <Route path="/digital-download" element={<DigitalDownload />} />
          <Route path="/consultation" element={<ConsultationBooking />} />
          <Route path="/book-consultation" element={<ConsultationBooking />} />
          <Route path="/support" element={<SupportNextXus />} />
          <Route path="/donate" element={<SupportNextXus />} />
        </Routes>
          </GoogleAuthGate>
        {/* 🜂 Dual-Mind Footer — REX & WEAVER (Museum Standard) */}
        <FederationTools hideOnSpeech>
        <DualMindFooter />
        <FederationFaces />
        <FederationThinBar />
        </FederationTools>
      </BrowserRouter>
      
      <NextXusContactBar />
      <Toaster />
    </div>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
